import { DataGrid } from '@mui/x-data-grid'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import * as React from 'react'
import { useQuery } from '@apollo/client'
import { getAllStyles } from '../../graphql/queries'
import ProgressBar from '../circularProgress/circularProgress'
import { Box, Button, Chip, TextField, FormHelperText } from '@mui/material'
import { useState, useEffect } from 'react'
import { useDataRefresh } from '../../hooks/useDataRefresh'

const columns = [
  {
    field: 'tableIndex',
    headerName: '#',
    flex: 0.2,
    minWidth: 150,
    filterable: false,
    sortable: false,
  },
  { field: 'name', headerName: 'Name', flex: 3 },
  {
    field: 'banner',
    headerName: 'Banner',
    flex: 1.5,
    renderCell: params => (
      <img
        src={params.value}
        alt="Avatar"
        style={{
          width: 'clamp(72px, 9vw, 100px)',
          height: 'clamp(72px, 9vw, 100px)',
          borderRadius: '50%',
          objectFit: 'cover',
          border: '1px solid #ddd',
        }}
      />
    ),
  },
  {
    field: 'isPublic',
    headerName: 'Public',
    flex: 1,
    renderCell: params => (
      <Chip
        label={params.value ? 'Yes' : 'No'}
        color={params.value ? 'success' : 'default'}
        size="small"
      />
    ),
  },
  {
    field: 'sections',
    headerName: 'Sections',
    flex: 2,
    renderCell: params => {
      const sections = params.value || []
      if (sections.length === 0) {
        return (
          <Chip
            label="No sections"
            color="default"
            size="small"
            variant="outlined"
          />
        )
      }
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: '100%' }}>
          {sections.slice(0, 2).map((section) => (
            <Chip
              key={section.id}
              label={section.name}
              color="primary"
              size="small"
              variant="outlined"
            />
          ))}
          {sections.length > 2 && (
            <Chip
              label={`+${sections.length - 2}`}
              color="default"
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      )
    },
  },
]

export default function StyleTable() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialPage = parseInt(searchParams.get('page')) || 0
  const initialPageSize = parseInt(searchParams.get('pageSize')) || 50
  const initialSearch = searchParams.get('search') || ''
  const [page, setPage] = useState(initialPage)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [searchTerm, setSearchTerm] = useState(initialSearch)
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearch)
  const [totalCount, setTotalCount] = useState(0)

  const navigate = useNavigate()

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 500) // 500ms delay

    return () => clearTimeout(timer)
  }, [searchTerm])

  // URL query param güncelleme
  useEffect(() => {
    const params = {}
    if (page !== 0) params.page = page.toString()
    if (pageSize !== 50) params.pageSize = pageSize.toString()
    if (debouncedSearchTerm) params.search = debouncedSearchTerm
    setSearchParams(params)
  }, [page, pageSize, debouncedSearchTerm, setSearchParams])

  const { data, loading, error, refetch } = useQuery(getAllStyles, {
    variables: {
      page: page + 1, // API 1-indexed ise
      pageSize,
      name: debouncedSearchTerm || null,
    },
    fetchPolicy: 'cache-and-network',
  })

  // Enable automatic data refresh when returning from detail pages
  useDataRefresh(refetch)

  // totalCount güncelle
  useEffect(() => {
    if (data?.getAllStylesAdmin?.totalCount) {
      setTotalCount(data.getAllStylesAdmin.totalCount)
    }
  }, [data])

  if (loading) return <ProgressBar />
  if (error) return <p>Error: {error.message}</p>

  const rowsData = data?.getAllStylesAdmin?.styles || []
  const rowsWithIndex = rowsData.map((row, index) => ({
    ...row,
    tableIndex: page * pageSize + index + 1,
  }))

  const handleRowClick = params => {
    navigate(`/styles/${params.id}`)
  }



  const handlePaginationModelChange = newModel => {
    setPage(newModel.page)
    setPageSize(newModel.pageSize)
  }

  const handleSearchChange = (event) => {
    const newSearchTerm = event.target.value
    setSearchTerm(newSearchTerm)
    setPage(0) // Reset to first page when searching
  }

  return (
    <Box style={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2, gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column-reverse' }}>
          <TextField
            variant="standard"
            placeholder="Search by style name..."
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ width: '300px' }}
          />
          <FormHelperText>Search styles</FormHelperText>
        </Box>
        <Link to="/styles/create">
          <Button
            sx={{ color: '#212529', '&:hover': { backgroundColor: '#212529', color: '#fff' } }}
          >
            Create
          </Button>
        </Link>
      </Box>
      <DataGrid
        rows={rowsWithIndex}
        columns={columns}
        rowHeight={110}
        disableColumnMenu
        paginationMode="server"
        page={page}
        onPageChange={newPage => setPage(newPage)}
        pageSize={pageSize}
        onPageSizeChange={newSize => {
          setPageSize(newSize)
          setPage(0)
        }}
        rowCount={totalCount}
        onRowClick={handleRowClick}
        rowsPerPageOptions={[25, 50, 100]}
        loading={loading}
        pagination
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[25, 50, 100]}
        sx={{
          height: 680,
          '& .MuiDataGrid-virtualScroller': {
            msOverflowY: 'auto',
          },
          fontSize: '16px',
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight) > .MuiDataGrid-cell': {
            whiteSpace: 'break-spaces',
          },
          '.MuiFormControl-root': {
            margin: '0px 0px 10px 0px',
            width: '100px',
          },
          '.MuiOutlinedInput-root': {
            width: '150px',
          },
          '.MuiToolbar-regular': {
            padding: 0,
          },
        }}
      />
    </Box>
  )
}
